// Body regions for the interactive chain map. Points are in the 240x520 SVG viewBox.
export const REGIONS = {
  head: {
    name: "Head & Brain", role: "Command center", points: [[120, 46]],
    note: "Your brain builds a live map of the body from every joint, muscle and fascial line below it — and sends back the tone, posture and protective patterns you feel.",
    down: "The brain sets muscle tone, balance strategy and posture for everything below.",
    up: "The final destination: the brain integrates every signal from the chain and decides how to respond.",
    links: ["neck", "jaw", "thoracic"],
  },
  jaw: {
    name: "Jaw (TMJ)", role: "Tension regulator", points: [[120, 70]],
    note: "The jaw shares nerve pathways and muscle attachments with the upper neck. Clenching, bite and head posture constantly influence each other — which is why jaw tension so often travels with neck pain and headaches.",
    links: ["head", "neck", "shoulder"],
  },
  neck: {
    name: "Cervical Spine", role: "Sensor-rich relay", points: [[120, 92]],
    note: "The upper neck holds one of the densest concentrations of position sensors in the body. It orients your eyes, balance and head — so stiffness here can echo into headaches, jaw tension and the shoulders.",
    down: "Every command to the arms and trunk passes through here first.",
    up: "Neck receptors tell the brain where the head sits in space, steering balance and eye control.",
    links: ["head", "jaw", "shoulder", "thoracic"],
  },
  shoulder: {
    name: "Shoulders & Scapula", role: "Mobile platform", points: [[76, 110], [164, 110]],
    note: "The shoulder blade floats on the ribcage. How well it moves depends on the thoracic spine below, the neck above — and, through cross-body fascial slings, the opposite hip.",
    links: ["neck", "thoracic", "hip"],
  },
  thoracic: {
    name: "Thoracic Spine & Ribs", role: "Rotation & breath", points: [[120, 148]],
    note: "The mid-back and ribcage are built to rotate and expand with every breath. When they stiffen, the neck and low back are forced to move more than they should — a classic hidden driver.",
    down: "Upper-back posture shapes breathing and how load is shared with the low back.",
    up: "Rib and mid-back mobility decide how much rotation reaches the neck and shoulders.",
    links: ["neck", "shoulder", "lumbar"],
  },
  lumbar: {
    name: "Lumbar Spine & Core", role: "Stability column", points: [[120, 205]],
    note: "The low back is designed for stability more than mobility. It often takes the strain when the hips, mid-back or feet aren't doing their share.",
    down: "Core timing from the nervous system protects the low back before you even move.",
    up: "Low-back load reflects how well the pelvis and hips absorb force from the ground.",
    links: ["thoracic", "pelvis", "hip"],
  },
  pelvis: {
    name: "Pelvis & Sacrum", role: "Keystone", points: [[120, 244]],
    note: "The pelvis is where the upper and lower body meet. Its position and the sacroiliac joints set the stage for the spine above and the hips below — and it's central in pregnancy and postpartum care.",
    down: "Trunk posture and breathing change pelvic position and pelvic-floor tone.",
    up: "The pelvis transfers ground force from the legs into the spine.",
    links: ["lumbar", "hip", "knee"],
  },
  hip: {
    name: "Hips", role: "Power engine", points: [[100, 266], [140, 266]],
    note: "The hips generate power and steer the knee's alignment. Restricted or weak hips quietly overload the low back above and the knee below.",
    down: "Pelvic control and core timing let the hips fire at the right moment.",
    up: "Hip control steers knee tracking and passes force into the pelvis.",
    links: ["pelvis", "lumbar", "knee", "shoulder"],
  },
  knee: {
    name: "Knees", role: "Hinge in the middle", points: [[97, 370], [143, 370]],
    note: "The knee is mostly a victim of its neighbours. It follows the hip above and the foot below — so knee pain is often a hip or foot story.",
    down: "Hip strength decides whether the knee tracks straight or collapses inward.",
    up: "Foot and ankle mechanics twist or steady the knee with every step.",
    links: ["hip", "foot"],
  },
  foot: {
    name: "Feet & Ankles", role: "Foundation & first sensor", points: [[96, 478], [144, 478]],
    note: "Your feet are your first contact with the ground and are loaded with sensors. How they absorb, adapt and push off sends a stream of information up the whole chain.",
    down: "The final link: top-down control decides how the foot lands, balances and pushes off.",
    up: "Ground contact starts here — foot position sets ankle, knee, hip and pelvic alignment.",
    links: ["knee", "hip", "pelvis"],
  },
};

export const TOP_DOWN = ["head", "neck", "thoracic", "lumbar", "pelvis", "hip", "knee", "foot"];
export const BOTTOM_UP = [...TOP_DOWN].reverse();

export const MODES = {
  explore: { label: "Explore", title: "Tap any region", body: "Select a link in the chain to see how its position shapes your posture, where it can show up as pain — and the chain reaction it can set off through the body." },
  down: { label: "Top-Down", title: "The brain controls the body", body: "The brain and spinal cord send control downward — setting tone, posture and movement strategy for every link below.", order: TOP_DOWN, key: "down" },
  up: { label: "Bottom-Up", title: "The body informs the brain", body: "Your feet and joints send information upward — every step tells the nervous system where you are and how to respond.", order: BOTTOM_UP, key: "up" },
};

// Concern slug → the primary region and the linked regions with a "why it connects" note.
export const CONCERN_CHAINS = {
  "back-pain": {
    primary: "lumbar",
    intro: "Low-back pain is rarely a low-back-only problem. The lumbar spine sits between a mid-back that should rotate and hips that should generate power — when either falls short, the low back pays the bill.",
    links: [
      { region: "hip", why: "Stiff or weak hips force the low back to bend and twist in their place." },
      { region: "pelvis", why: "Pelvic position and sacroiliac motion set the angle and load of the lumbar spine." },
      { region: "thoracic", why: "A stiff mid-back pushes rotation down into lumbar joints not built for it." },
      { region: "foot", why: "How your feet absorb ground force changes the load that reaches your spine." },
    ],
  },
  "neck-pain": {
    primary: "neck",
    intro: "The neck carries your head and houses some of the body's most important position sensors. It's often overworking for a stiff upper back, a tense jaw or shoulders that have lost their glide.",
    links: [
      { region: "thoracic", why: "When the mid-back doesn't rotate or extend, the neck makes up the difference." },
      { region: "shoulder", why: "Upper-trap and shoulder-blade muscles anchor directly onto the neck." },
      { region: "jaw", why: "Jaw clenching and upper-neck muscles share nerve pathways and tension." },
      { region: "head", why: "Head posture and visual strain keep the neck's stabilisers constantly switched on." },
    ],
  },
  "hip-pain": {
    primary: "hip",
    intro: "The hips are the body's power engine, sitting between the pelvis and the knee. Hip pain often reflects how the pelvis is positioned and how the foot and knee are steering the leg below.",
    links: [
      { region: "pelvis", why: "Pelvic tilt and SI-joint motion change how the hip socket is loaded." },
      { region: "lumbar", why: "Low-back nerve roots and muscles refer into the hip and buttock." },
      { region: "knee", why: "Knee alignment twists the thigh and changes hip rotation." },
      { region: "foot", why: "Foot collapse rotates the whole leg inward, straining the hip." },
    ],
  },
  "knee-pain": {
    primary: "knee",
    intro: "The knee is a hinge caught between two bosses: the hip above and the foot below. When either loses control, the knee absorbs the twist.",
    links: [
      { region: "hip", why: "Weak hip stabilisers let the knee drift inward under load." },
      { region: "foot", why: "Ankle stiffness and foot collapse transmit rotation straight into the knee." },
      { region: "pelvis", why: "Pelvic control sets the angle of the thigh feeding into the knee." },
    ],
  },
  "shoulder-pain": {
    primary: "shoulder",
    intro: "Your shoulder blade glides on the ribcage and is wired through the neck. Shoulder pain often traces back to a stiff mid-back, irritated neck, or a broken cross-body sling to the opposite hip.",
    links: [
      { region: "thoracic", why: "A rounded, stiff upper back narrows the space the shoulder needs to move." },
      { region: "neck", why: "Nerves to the shoulder exit the neck — neck irritation can show up as shoulder pain." },
      { region: "hip", why: "Fascial slings connect each shoulder to the opposite hip in walking, lifting and throwing." },
    ],
  },
  headaches: {
    primary: "head",
    intro: "Many headaches are driven from below. The upper neck, jaw and shoulder muscles feed directly into the pain-processing centers of the head — calm the chain and the head often follows.",
    links: [
      { region: "neck", why: "Upper-neck joints and muscles share a nerve hub with the head and refer pain upward." },
      { region: "jaw", why: "Clenching and TMJ tension load the temples and the same trigeminal pathways." },
      { region: "shoulder", why: "Tight upper traps pull on the base of the skull." },
      { region: "thoracic", why: "Mid-back posture and shallow breathing keep neck muscles overworking." },
    ],
  },
  sciatica: {
    primary: "lumbar",
    intro: "Sciatic symptoms travel along a nerve that runs from the low back through the pelvis and down the leg. Anywhere along that path — spine, pelvis, hip — can irritate or protect it.",
    links: [
      { region: "pelvis", why: "The sciatic nerve passes through the pelvis, where SI and muscle tension can irritate it." },
      { region: "hip", why: "Deep hip rotators sit right on the nerve and can compress it when overworked." },
      { region: "knee", why: "The nerve continues behind the knee — symptoms often follow its path." },
      { region: "foot", why: "Foot mechanics change leg tension and how the nerve slides with each step." },
    ],
  },
  "sports-injuries": {
    primary: "pelvis",
    intro: "Athletic force travels from the ground, through the pelvis, to the arms. Injuries often happen at the link where that transfer breaks down — even if it hurts somewhere else.",
    links: [
      { region: "foot", why: "Landing and cutting begin at the foot — poor absorption sends shock up the chain." },
      { region: "knee", why: "Knees take the twist when the foot or hip lose control." },
      { region: "hip", why: "Hip power and stability decide how well force reaches the trunk." },
      { region: "shoulder", why: "Throwing and swinging rely on force passed from the opposite hip." },
    ],
  },
  "pregnancy-discomfort": {
    primary: "pelvis",
    intro: "As baby grows, the pelvis widens, ligaments soften and your centre of gravity moves forward. The whole chain adapts — and the pelvis sits right at the centre of it.",
    links: [
      { region: "lumbar", why: "A forward-shifting centre of gravity increases the curve and load of the low back." },
      { region: "hip", why: "Hip muscles work harder to stabilise a looser, wider pelvis." },
      { region: "thoracic", why: "Ribs expand and the mid-back adapts to make room for baby and breath." },
      { region: "foot", why: "Changing posture and weight shift how your feet carry and balance you." },
    ],
  },
  "postpartum-recovery": {
    primary: "pelvis",
    intro: "Recovery after birth is a whole-body project. The pelvis and core are rebuilding while feeding, carrying and broken sleep load the upper back and neck.",
    links: [
      { region: "lumbar", why: "Core and pelvic-floor coordination are rebuilding to protect the low back again." },
      { region: "thoracic", why: "Feeding and carrying postures round the upper back for hours each day." },
      { region: "shoulder", why: "Lifting and holding baby loads the shoulders and shoulder blades." },
      { region: "neck", why: "Looking down at baby keeps neck muscles under constant load." },
    ],
  },
  "pediatric-care": {
    primary: "head",
    intro: "A child's nervous system is learning at an incredible pace — head control, rolling, crawling and walking all build the map. Gentle care supports how the whole chain develops and communicates.",
    links: [
      { region: "neck", why: "Upper-neck motion supports head control, feeding position and comfort." },
      { region: "thoracic", why: "Rib and mid-back motion support breathing, rolling and crawling." },
      { region: "pelvis", why: "Pelvic balance supports sitting, crawling and early walking." },
      { region: "foot", why: "Feet are a major sensory input as children learn to stand and balance." },
    ],
  },
  "mobility-restrictions": {
    primary: "hip",
    intro: "Stiffness in one link forces others to move more. Restoring mobility means finding the joints that have stopped sharing the work — often the hips, mid-back and ankles.",
    links: [
      { region: "thoracic", why: "A stiff mid-back limits reaching, rotating and breathing." },
      { region: "foot", why: "Limited ankle motion changes squatting, stairs and walking all the way up." },
      { region: "lumbar", why: "The low back ends up moving for the stiff joints around it." },
      { region: "shoulder", why: "Overhead reach depends on both the shoulder and the ribcage beneath it." },
    ],
  },
  "recurring-tension": {
    primary: "shoulder",
    intro: "Muscles that stay tight are usually protecting something. Recurring tension is often the nervous system guarding a link that isn't moving or being controlled well.",
    links: [
      { region: "neck", why: "Neck irritation keeps the upper traps and shoulder muscles on guard." },
      { region: "jaw", why: "Stress and clenching add constant tone to the jaw, neck and shoulders." },
      { region: "thoracic", why: "Shallow, chest-driven breathing overworks neck and shoulder muscles." },
      { region: "lumbar", why: "A poorly timed core makes larger muscles brace to compensate." },
    ],
  },
};

export const CLINICAL_STEPS = [
  { name: "Listen", title: "Your story comes first.", body: "We start with a real conversation: what hurts, when it started, what you've tried, and what you want to get back to. Your goal becomes our goal.", tags: ["Health history", "Goals", "Lifestyle & load"] },
  { name: "Assess", title: "We test, we don't guess.", body: "Movement screens, orthopedic and neurological testing, strength and joint motion — from the feet to the skull — to see how your whole chain is moving and being controlled.", tags: ["Movement", "Strength", "Neuro & ortho testing"] },
  { name: "Find the Pattern", title: "Connect the dots.", body: "Where pain shows up is rarely where it starts. We map your findings onto the chain to find the link that is overloaded, under-moving or poorly controlled.", tags: ["Root cause", "Compensation map", "Your plan"] },
  { name: "Treat", title: "Change the input.", body: "Adjustments, soft-tissue and functional therapies chosen for your pattern — each one aimed at improving the information your nervous system receives.", tags: ["Chiropractic", "Soft tissue", "Modalities"] },
  { name: "Reassess", title: "Prove it changed.", body: "We retest your key findings in the same visit so you can feel and see the change — and every next step is guided by results, not routine.", tags: ["Retest", "Measure", "Refine the plan"] },
  { name: "Restore", title: "Make it last.", body: "Targeted rehab and home care train the new pattern until your body owns it — so you can move, sleep, train and live with confidence.", tags: ["Rehab", "Home program", "Return to life"] },
];

// Draws lines between every point of region a and region b (bilateral regions pair by side).
export function segments(a, b) {
  const pa = REGIONS[a].points;
  const pb = REGIONS[b].points;
  if (pa.length === pb.length) return pa.map((p, i) => [p, pb[i]]);
  const [many, one] = pa.length > pb.length ? [pa, pb[0]] : [pb, pa[0]];
  return many.map((p) => [p, one]);
}
